import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import startCase from 'lodash.startcase';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { uuid } from 'uuidv4';

import PortfolioSection from '../Section/PortfolioSection';
import RecentTransactionCard from './RecentTransactionsCard';
import TransactionDetails from '../../Modals/Transaction/TransactionDetails';
import Modal from '../../Modal/Modal';
import SellFromHomeTransactionDetails from '../../Modals/SellWizard/SellFromHomeTransactionDetails';
import SellFromHomeTransactionHistory from '../../Modals/SellWizard/SellFromHomeTransactionHistory';
import SfhModalTitle from '../../Modals/SellWizard/SfhModalTitle';
import {
  accountTransactionsFetch,
  emptyPortfolioTransactionSingle,
  portfolioTransactionsSingleFetch,
  portfolioFinancialPendingTransactions,
} from '../../../store/actions/portfolio/portfolioActions';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../../store/selectors/portfolioTransactionsSelector';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { GET_TRIGGER_ORDER_DETAILS_LOADING } from '../../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { getTriggerOrderDetails } from '../../../store/actions/triggerOrders/triggerOrdersActions';
import {
  getSingleTransactionType,
  isTargetPriceOrderTransaction,
  mergeTriggerOrderDetails,
} from '../../../util/helpers/transcation/singleTransactionHelper';
import { formatDate } from '../../../util/helpers/dateHelpers';
import {
  asTargetPriceOrderSummary,
  transactionFieldFormatter,
  getInsiderSavingsSubtext,
} from '../../../util/helpers/transcation/transactionHelper';
import BlockSectionLoader from '../../Loader/BlockSectionLoader';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import SectionLoader from '../../Loader/SectionLoader';
import { EmptyTable } from '../../EmptyTable/EmptyTable';
import { uFlexColumn } from '../../../assets/styles/utility';
import { variables } from '../../../assets/styles/variables';
import PendingTransactionCard from './PendingTransactionCard';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { productLocationTypes } from '../../../util/enum/api/productTypes';
import {
  transactionRequiestStatusTypesArray,
  transactionTypes,
  triggerOrderStatusTypesArray,
  SELL_FROM_HOME,
  TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER,
} from '../../../util/enum/api/transactionTypes';
import { RECENT_TRANSACTIONS } from '../../../constants/accountTransactionConstants';
import CancelPendingTransactionModal from '../../Modals/CancelPendingTransaction/CancelPendingTransactionModal';
import CancelTriggerOrderModal from '../../Modals/CancelTriggerOrder/CancelTriggerOrderModal';
import TriggerOrderDetailsModal from '../../Modals/TriggerOrderDetails/TriggerOrderDetailsModal';
import { pxToRem } from '../../../assets/styles/helper';

const RecentTransaction = styled.div`
  ${uFlexColumn};
  box-shadow: ${variables.shadow.boxShadow};
  border-radius: ${variables.borderRadius.borderRadius};
`;

const RecentTransactions = ({
  data,
  pendingTransactions,
  error,
  accountUid,
  isLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(
    false,
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelTransactionData, setCancelTransactionData] = useState({});
  const [sfhTransactionUid, setSfhTransactionUid] = useState(null);
  const [isSfhHistoryView, setIsSfhHistoryView] = useState(false);
  const [
    isCancelTriggerOrderModalOpen,
    setIsCancelTriggerOrderModalOpen,
  ] = useState(false);
  const [cancelTriggerOrderData, setCancelTriggerOrderData] = useState(null);
  const [isTriggerOrderDetailsOpen, setIsTriggerOrderDetailsOpen] = useState(
    false,
  );
  const [selectedTriggerOrder, setSelectedTriggerOrder] = useState(null);
  // Whether the order in the modal is a canceled or expired one, which the
  // modal words and lays out differently from an Active one.
  const [isEndedTriggerOrder, setIsEndedTriggerOrder] = useState(false);
  // Whether the transaction in TransactionDetails is one of a Target Price
  // order's rows (its reservation, say), which gets a Close button at the bottom.
  const [isTargetPriceOrderDetails, setIsTargetPriceOrderDetails] = useState(
    false,
  );
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );
  const isOrderDetailsLoading = useSelector(
    selectIsLoadingByActionType(GET_TRIGGER_ORDER_DETAILS_LOADING),
  );
  const { SegregatedCaption } = useSelector(selectSettings);

  const onTransactionCardClick = useCallback(
    (transaction) => {
      // Sell From Home rows open the dedicated SFH details modal, which fetches
      // the single transaction itself from the Uid it is handed. The recent
      // transactions feed is unmapped, so Type arrives as the raw number (39);
      // also accept the mapped string in case a mapped feed is used.
      if (
        transaction.Type === SELL_FROM_HOME ||
        transaction.Type === transactionTypes.SellFromHome
      ) {
        setSfhTransactionUid(transaction.Uid);
        return;
      }
      const transactionUid = transaction.Uid;
      const type = getSingleTransactionType(transaction);

      if (type) {
        // A canceled or expired order opens in its own modal once it has
        // loaded (the section's loader shows meanwhile), as on the Financial
        // Balances table. The response lands in the store TransactionDetails
        // opens off, so make sure that one stays closed.
        setIsTransactionDetailsOpen(false);
        dispatch(
          portfolioTransactionsSingleFetch({
            accountUid,
            transactionUid,
            type,
            onSuccess: (details) => {
              setSelectedTriggerOrder(details);
              setIsEndedTriggerOrder(true);
              setIsTriggerOrderDetailsOpen(true);
            },
          }),
        );
        return;
      }

      dispatch(
        portfolioTransactionsSingleFetch({
          accountUid,
          transactionUid: transaction.Uid,
        }),
      );
      setIsTargetPriceOrderDetails(isTargetPriceOrderTransaction(transaction));
      setIsTransactionDetailsOpen(true);
    },
    [accountUid, dispatch],
  );

  const handleSfhDetailsClose = useCallback(() => {
    dispatch(emptyPortfolioTransactionSingle());
    setSfhTransactionUid(null);
    setIsSfhHistoryView(false);
  }, [dispatch]);

  const handleSfhViewHistory = useCallback(() => setIsSfhHistoryView(true), []);
  const handleSfhHistoryBack = useCallback(
    () => setIsSfhHistoryView(false),
    [],
  );

  const handleCancelModalOpen = (accountUid, paymentUid) => {
    setIsCancelModalOpen(true);
    setCancelTransactionData({
      accountUid,
      paymentUid,
      content: RECENT_TRANSACTIONS,
    });
  };

  const refreshTransactions = useCallback(() => {
    dispatch(
      portfolioFinancialPendingTransactions({
        accountUid,
        content: RECENT_TRANSACTIONS,
      }),
    );
  }, [accountUid, dispatch]);

  // Same fetch PortfolioPage makes for this list on load.
  const refreshRecentTransactions = useCallback(() => {
    dispatch(
      accountTransactionsFetch({
        accountUid,
        content: RECENT_TRANSACTIONS,
      }),
    );
  }, [accountUid, dispatch]);

  const handleCancelTriggerOrderModalOpen = useCallback(
    (triggerOrderAccountUid, triggerOrderUid, amount, transaction) => {
      setCancelTriggerOrderData({
        accountUid: triggerOrderAccountUid,
        paymentUid: triggerOrderUid,
        amount,
        content: RECENT_TRANSACTIONS,
        refreshTransactions,
        productName: transaction?.ProductCaption,
        requestDate: transaction?.RequestDate,
        quantity: transaction?.Quantity,
      });
      setIsCancelTriggerOrderModalOpen(true);
    },
    [refreshTransactions],
  );

  const handleCancelTriggerOrderModalClose = () => {
    setIsCancelTriggerOrderModalOpen(false);
  };

  const showTriggerOrderDetails = (transaction) => {
    setSelectedTriggerOrder(transaction);
    setIsEndedTriggerOrder(false);
    setIsTriggerOrderDetailsOpen(true);
  };

  // Opens the order the way the Financial Balances table does, so the modal
  // shows the same rows. An Active order's details (the prices, reference
  // number, ...) come from getTriggerOrderDetails, and the modal opens once they
  // have loaded, with the section's loader showing meanwhile. The row's own
  // fields win over the response (mergeTriggerOrderDetails), and if the call
  // fails the modal still opens with the row's data. Other statuses open
  // straight away.
  const handleTriggerOrderDetailsOpen = useCallback(
    (transaction) => {
      const status = parseEnumType(
        triggerOrderStatusTypesArray,
        transaction.TriggerOrderStatus,
      );

      if (status !== 'Active' || !transaction.TriggerOrderUid) {
        showTriggerOrderDetails(transaction);
        return;
      }

      dispatch(
        getTriggerOrderDetails({
          accountUid,
          triggerOrderUid: transaction.TriggerOrderUid,
          onSuccess: (details) =>
            showTriggerOrderDetails(
              mergeTriggerOrderDetails(details, transaction),
            ),
          onError: () => showTriggerOrderDetails(transaction),
        }),
      );
    },
    [accountUid, dispatch],
  );

  const handleTriggerOrderDetailsClose = () => {
    setIsTriggerOrderDetailsOpen(false);
    dispatch(emptyPortfolioTransactionSingle());
  };

  const handleCancelModalClose = () => {
    setIsCancelModalOpen(false);
    setCancelTransactionData({});
  };

  const renderTransactions = useCallback(() => {
    const renderBody = () => {
      const transactions = [];
      if (!isEmpty(data) && !!data.length) {
        transactions.push(...data);
      }
      if (!isEmpty(pendingTransactions) && !!pendingTransactions.length) {
        transactions.push(
          ...pendingTransactions?.map((transaction) => ({
            ...transaction,
            DateTime: transaction.RequestDate,
          })),
        );
      }
      const sortedTransactions = transactions.sort((a, b) => {
        const dateA = new Date(a.DateTime);
        const dateB = new Date(b.DateTime);
        return dateB - dateA;
      });
      const renderTransactionCard = (transaction) => {
        if (
          transaction.TransactionRequestType ===
          TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER
        ) {
          const unit = t(
            transaction.Quantity > 1
              ? 'portfolio.transactions.details.pluralUnit'
              : 'portfolio.transactions.details.singleUnit',
          );

          return (
            <PendingTransactionCard
              key={`${transaction.TriggerOrderUid}-${uuid()}`}
              month={formatDate(transaction.DateTime, 'MMMM')}
              day={formatDate(transaction.DateTime, 'd')}
              amount={transaction.Amount}
              label={t('portfolio.pendingTransactions.details.summary', {
                quantity: transaction.Quantity,
                unit,
              })}
              product={transaction.ProductCaption}
              isSegregated={transaction.IsSegregated}
              segregatedCaption={SegregatedCaption}
              vaultName={startCase(
                parseEnumType(productLocationTypes, transaction.LocationType),
              )}
              status={parseEnumType(
                triggerOrderStatusTypesArray,
                transaction.TriggerOrderStatus,
              )}
              canCancel={transaction.CanCancel}
              handleModalOpen={() =>
                handleCancelTriggerOrderModalOpen(
                  accountUid,
                  transaction.TriggerOrderUid,
                  transaction.Amount,
                  transaction,
                )
              }
              onCardClick={() => handleTriggerOrderDetailsOpen(transaction)}
              isTriggerOrder
            />
          );
        }

        if (transaction.DepositKey) {
          return (
            <PendingTransactionCard
              key={`${transaction.DepositKey}-${uuid()}`}
              month={formatDate(transaction.DateTime, 'MMMM')}
              day={formatDate(transaction.DateTime, 'd')}
              amount={transaction.Amount}
              status={parseEnumType(
                transactionRequiestStatusTypesArray,
                transaction.RequestStatus,
              )}
              canCancel={transaction.CanCancel}
              action={transaction.TransactionType}
              handleModalOpen={() =>
                handleCancelModalOpen(accountUid, transaction.DepositKey)
              }
            />
          );
        }

        return (
          <RecentTransactionCard
            key={`${transaction.Uid}_${transaction.DateTime}`}
            month={formatDate(transaction.DateTime, 'MMMM')}
            day={formatDate(transaction.DateTime, 'd')}
            action={
              transaction.Fields[0]
                ? transactionFieldFormatter(
                    // A buy the BE flags as a Target Price Order reads as the
                    // order does (IsTriggerOrder).
                    transaction.IsTriggerOrder
                      ? asTargetPriceOrderSummary(transaction.Fields[0])
                      : transaction.Fields[0],
                    { isRow: true },
                  )
                : null
            }
            actionSubject={
              transaction.Fields[1]
                ? transactionFieldFormatter(transaction.Fields[1])
                : null
            }
            discountSubtext={
              transaction.Fields
                ? getInsiderSavingsSubtext(transaction.Fields)
                : null
            }
            isSegregated={transaction.IsSegregated}
            status={transaction.StatusType}
            isTargetPriceOrder={isTargetPriceOrderTransaction(transaction)}
            onClick={() => onTransactionCardClick(transaction)}
          />
        );
      };

      return !isEmpty(sortedTransactions) && !!sortedTransactions.length
        ? sortedTransactions.map(renderTransactionCard)
        : null;
    };

    return (
      <>
        {isLoading ? (
          <BlockSectionLoader isLoading={isLoading} />
        ) : (
          <>
            {renderBody()}
            {(isEmpty(data) || !data.length) &&
              (isEmpty(pendingTransactions) || !pendingTransactions.length) && (
                <EmptyTable>
                  <p>{t('portfolio.transactions.tableEmpty')}</p>
                </EmptyTable>
              )}
          </>
        )}
      </>
    );
  }, [
    data,
    pendingTransactions,
    accountUid,
    isLoading,
    onTransactionCardClick,
    handleCancelTriggerOrderModalOpen,
    handleTriggerOrderDetailsOpen,
    SegregatedCaption,
    t,
  ]);

  return (
    <PortfolioSection title="Recent Transactions">
      <RecentTransaction>
        {!error ? (
          <SectionLoader
            isLoading={singleTransactionLoading || isOrderDetailsLoading}
          >
            {renderTransactions()}
          </SectionLoader>
        ) : (
          <span>{error}</span>
        )}
      </RecentTransaction>
      <TransactionDetails
        isOpen={!isEmpty(singleTransaction) && isTransactionDetailsOpen}
        onClose={() => {
          dispatch(emptyPortfolioTransactionSingle());
          setIsTransactionDetailsOpen(false);
        }}
        transaction={singleTransaction}
        error={singleTransactionError}
        isTargetPriceOrder={isTargetPriceOrderDetails}
      />
      <CancelPendingTransactionModal
        handleClose={handleCancelModalClose}
        isOpen={isCancelModalOpen}
        cancelTransactionData={cancelTransactionData}
      />
      <CancelTriggerOrderModal
        isOpen={isCancelTriggerOrderModalOpen}
        handleClose={handleCancelTriggerOrderModalClose}
        cancelTransactionData={cancelTriggerOrderData}
        handleRefreshTransactions={refreshRecentTransactions}
      />
      <TriggerOrderDetailsModal
        isOpen={isTriggerOrderDetailsOpen}
        onClose={handleTriggerOrderDetailsClose}
        transaction={selectedTriggerOrder}
        isEnded={isEndedTriggerOrder}
      />
      <Modal
        isOpen={Boolean(sfhTransactionUid)}
        close={handleSfhDetailsClose}
        goBack={isSfhHistoryView ? handleSfhHistoryBack : undefined}
        title={
          <SfhModalTitle>
            {t(
              isSfhHistoryView
                ? 'sellWizard.sfhWizard.transactionHistory.title'
                : 'sellWizard.sfhWizard.transactionDetails.title',
            )}
          </SfhModalTitle>
        }
        headerBordered
        size="sm"
        maxWidth={pxToRem(640)}
        minHeight={pxToRem(640)}
        height={pxToRem(640)}
        clipContent
        closeIconVisible
        skipListeningToLocations
        dataCy="container-sfh-transaction-details"
      >
        {sfhTransactionUid &&
          (isSfhHistoryView ? (
            <SellFromHomeTransactionHistory
              lockedSnapshot={{ transactionUid: sfhTransactionUid }}
              handleClose={handleSfhDetailsClose}
            />
          ) : (
            <SellFromHomeTransactionDetails
              lockedSnapshot={{ transactionUid: sfhTransactionUid }}
              handleClose={handleSfhDetailsClose}
              handleViewHistory={handleSfhViewHistory}
              source="Recent Transactions"
            />
          ))}
      </Modal>
    </PortfolioSection>
  );
};

RecentTransactions.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  pendingTransactions: PropTypes.arrayOf(PropTypes.shape({})),
  error: PropTypes.string,
  accountUid: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default RecentTransactions;
